import React, { useState } from "react";
import { Download, FileText, ChevronRight, X, ThumbsUp, ThumbsDown, CheckCircle2, ListFilter } from "lucide-react";
import { TID } from "@/lib/testIds";
import ActionButton from "./ActionButton";

const money = (v) =>
  v == null || v === "" || isNaN(Number(v))
    ? "—"
    : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Format a date-only string ("2026-05-02") as "May 2, 2026" without timezone drift.
const fmtDate = (v) => {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  }
  return String(v);
};
const fmtDateShort = (v) => {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  return String(v);
};

// Reconstruct the full group-prefixed CARC code (e.g. group "CO" + reason "45" → "CO-45").
// Some payers already return the combined form; keep it as-is when it has a letter prefix.
const fullCode = (a = {}) => {
  const code = String(a.adjustment_code || a.reason_code || "").trim();
  const grp = String(a.group_code || "").trim();
  if (!code) return grp || "";
  if (/^[A-Za-z]/.test(code)) return code.toUpperCase();
  return grp ? `${grp}-${code}` : code;
};
const isPR = (a = {}) => /^PR/i.test(fullCode(a)) || /^PR/i.test(String(a.group_code || ""));

// Balancing (matches the reference ERA claim view): a line balances when
// billed = paid + patient_responsibility + Σ (contractual adjustments). PR-group
// adjustments ARE the patient responsibility, so they aren't double-counted.
const num = (v) => Number(v || 0);
const lineAdjTotal = (l) => (l.adjustments || []).reduce((s, a) => s + Math.abs(num(a.amount)), 0);
const lineContractualAdj = (l) => (l.adjustments || []).reduce((s, a) => s + (isPR(a) ? 0 : Math.abs(num(a.amount))), 0);
const linePrTotal = (l) => (l.adjustments || []).reduce((s, a) => s + (isPR(a) ? Math.abs(num(a.amount)) : 0), 0);
const lineBalanced = (l) => {
  const ptResp = Math.max(num(l.patient_responsibility), linePrTotal(l));
  return Math.abs(num(l.billed_amount) - (num(l.paid_amount) + ptResp + lineContractualAdj(l))) < 0.02;
};
const claimBalanced = (c) => {
  const lines = c.service_lines || [];
  return lines.length > 0 && lines.every(lineBalanced);
};

const LINE_COLS = "grid-cols-[58px_minmax(54px,1fr)_38px_36px_repeat(5,minmax(50px,0.85fr))_minmax(46px,0.8fr)_26px]";

// Classify a CARC/RARC adjustment code into the reference's decoded "grammar".
// PR* → patient (amber), denial-ish → appeal (red), everything else → contractual write-off (gray).
const DENIAL_CODES = new Set(["197", "50", "96", "16", "29", "27", "204", "151"]);
function decClass(code = "", groupHint = "") {
  const c = String(code).toUpperCase();
  const num = (c.match(/(\d+)/) || [])[1] || "";
  const isPR = c.startsWith("PR") || /patient/i.test(groupHint);
  if (isPR) return "pat";
  if (DENIAL_CODES.has(num) && (c.startsWith("CO") || c.startsWith("PI") || !c.startsWith("PR"))) return "appeal";
  return "wo";
}
const DEC_STYLES = {
  wo: "bg-black/[.05] text-[#6e6e73]",
  pat: "bg-[#fbf1de] text-[#b06f00]",
  appeal: "bg-[#fbe9e7] text-[#d23b2f]",
  sec: "bg-[#f2ecfb] text-[#7a4fc0]",
  paid: "bg-[#e7f6ee] text-[#1f9d57]",
};

function Dec({ code, desc, cls, arrow }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-[6px] px-[7px] py-[2px] text-[9px] font-bold whitespace-nowrap ${DEC_STYLES[cls] || DEC_STYLES.wo}`}>
      <span className="font-mono font-extrabold">{code}</span>
      {desc ? (arrow ? <><span className="opacity-50">→</span>{desc}</> : <span className="opacity-70">{desc}</span>) : null}
    </span>
  );
}

// Determine claim status → dot + badge (from parsed status text, falling back to amounts).
function claimStatus(c) {
  const s = String(c.claim_status || "").toLowerCase();
  const paid = Number(c?.claim_totals?.paid_amount || 0);
  const billed = Number(c?.claim_totals?.billed_amount || 0);
  // Honor an explicit parsed status first, then fall back to amounts.
  if (/deny|denied|reject/.test(s)) return "denied";
  if (/partial/.test(s)) return "partial";
  if (/match|paid|process|complete|primary|adjudicat/.test(s)) return "matched";
  if (billed > 0 && paid === 0) return "denied";
  if (paid > 0 && paid < billed) return "partial";
  return "matched";
}
const STATE_DOT = { matched: "bg-[#1f9d57]", partial: "bg-[#2563a8]", denied: "bg-[#d23b2f]" };
const STATE_BADGE = {
  matched: "bg-[#e7f6ee] text-[#1f9d57]",
  partial: "bg-[#e8f0f9] text-[#2563a8]",
  denied: "bg-[#fbe9e7] text-[#d23b2f]",
};
const STATE_LABEL = { matched: "Matched", partial: "Partial", denied: "Denied" };

const FAN_COLS = "grid-cols-[22px_1.6fr_0.6fr_0.9fr_1.5fr_0.8fr_20px]";

export default function EraViewModal({ analysis, eraText, filename, originalFile, onDownload, onChooseFlow, onFinish, variant = "experience", showRaw: showRawProp, onToggleRaw, hideToolbar = false }) {
  const [showRawInternal, setShowRawInternal] = useState(false);
  const showRaw = showRawProp !== undefined ? showRawProp : showRawInternal;
  const toggleRaw = onToggleRaw || (() => setShowRawInternal((v) => !v));
  const [open, setOpen] = useState(() => new Set([0]));
  const [feedback, setFeedback] = useState(null);
  const [activeCode, setActiveCode] = useState(null);

  const ex = analysis?.extraction || {};
  const payer = ex.payer_info || {};
  const payee = ex.payee_info || {};
  const pay = ex.payment_info || {};
  const totals = ex.totals || {};
  const claims = Array.isArray(ex.claims) ? ex.claims : [];
  const hrs = ex.human_readable_summary || {};

  const totalLines = claims.reduce((n, c) => n + (Array.isArray(c.service_lines) ? c.service_lines.length : 0), 0);
  const deniedCount = claims.filter((c) => claimStatus(c) === "denied").length;
  const contract = pay.contract || payer.plan_name || pay.plan || null;
  const denialNotes = Array.isArray(hrs.denial_reasons) ? hrs.denial_reasons : [];

  // Unique CARC codes on this check, with the total adjustment amount per code and
  // the set of claim indices each code appears on (for click-to-filter).
  const legendMap = new Map();
  claims.forEach((c, ci) =>
    (c.service_lines || []).forEach((l) =>
      (l.adjustments || []).forEach((a) => {
        const code = fullCode(a);
        if (!code) return;
        let e = legendMap.get(code);
        if (!e) { e = { code, desc: a.description || "", cls: decClass(code, a.group_code), total: 0, claims: new Set() }; legendMap.set(code, e); }
        e.total += Math.abs(num(a.amount));
        e.claims.add(ci);
        if (!e.desc && a.description) e.desc = a.description;
      })
    )
  );
  const legend = Array.from(legendMap.values());

  const openOriginal = () => {
    if (!originalFile) return;
    const url = URL.createObjectURL(originalFile);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const toggle = (i) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  // Click-to-filter: keep original claim indices so open/toggle + test ids stay stable.
  const activeEntry = activeCode ? legendMap.get(activeCode) : null;
  const visibleClaims = claims
    .map((c, ci) => ({ c, ci }))
    .filter(({ ci }) => !activeEntry || activeEntry.claims.has(ci));
  const toggleCode = (code) => setActiveCode((cur) => (cur === code ? null : code));

  return (
    <div data-testid={TID.eraViewModal} className="flex flex-col text-[#1d1d1f]">
      {/* HEADER */}
      {!hideToolbar ? (
      <div className="sticky top-0 z-20 bg-white flex items-start gap-3.5 px-[22px] pt-[18px] pb-3.5 border-b border-black/[.08]">
        <div className="w-[38px] h-[38px] rounded-[11px] bg-[#1d1d1f] text-white flex items-center justify-center flex-shrink-0">
          <FileText size={20} strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[18px] font-extrabold tracking-[-.02em]">revQ converted this EOB → ERA</div>
          <div className="text-[11px] text-[#6e6e73] mt-1 leading-[1.5] max-w-[680px]">
            Lens read this remit, <b className="text-[#1d1d1f] font-semibold">decoded every CARC code</b>, and drafted a balanced{" "}
            <b className="text-[#1d1d1f] font-semibold">ERA (835)</b> across{" "}
            <b className="text-[#1d1d1f] font-semibold">{claims.length} claim{claims.length === 1 ? "" : "s"}</b>. Expand any claim to see its lines.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <ActionButton onClick={onDownload} variant="solid" icon={Download} data-testid={TID.eraViewDownloadBtn}>
            Download ERA (835)
          </ActionButton>
          {originalFile ? (
            <ActionButton onClick={openOriginal} icon={FileText} data-testid={TID.eraViewSourceBtn}>
              Source PDF
            </ActionButton>
          ) : null}
          {eraText ? (
            <ActionButton onClick={toggleRaw} data-testid={TID.eraViewRawToggle}>
              {showRaw ? "Hide 835" : "Raw 835"}
            </ActionButton>
          ) : null}
          {variant === "experience" ? (
            <button onClick={onFinish} data-testid={TID.eraViewClose}
              aria-label="Close" className="w-8 h-8 rounded-full bg-black/[.05] hover:bg-black/[.1] text-[var(--gray1)] flex items-center justify-center transition flex-shrink-0">
              <X size={16} />
            </button>
          ) : null}
        </div>
      </div>
      ) : null}

      {/* BODY */}
      <div className={hideToolbar ? "px-0 py-4" : "px-[22px] py-4"}>
        {showRaw && eraText ? (
          <pre className="mb-4 max-h-[280px] overflow-auto rounded-[10px] bg-[#1d1d1f] text-white/90 text-[10px] leading-[1.6] p-3 whitespace-pre-wrap break-all">{eraText}</pre>
        ) : null}

        {/* Money row */}
        <div className="flex gap-2.5 mb-3.5 flex-wrap">
          <Money label="Billed" value={money(totals.total_billed)} note={`${claims.length} claims · ${totalLines} lines`} />
          <Money label="Allowed" value={money(totals.total_allowed)} tone="blu" />
          <Money label="Paid" value={money(totals.total_paid)} tone="grn" note={payer.payer_name ? `check · ${payer.payer_name}` : "check"} />
          <Money label="Adjustments" value={money(totals.total_adjustments)} tone="amb" note={legend.slice(0, 3).map((l) => l.code).join(" · ") || null} />
          <Money label="Patient resp." value={money(totals.total_patient_responsibility)} note="→ routes to billing" />
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_232px] gap-4">
          {/* LEFT */}
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[8.5px] font-extrabold tracking-[.05em] uppercase text-[#6e6e73] mb-2.5">
              Posting breakdown · ERA (835)
            </div>

            {/* Remittance + payee detail — biller-critical fields */}
            <div className="border border-black/[.08] rounded-[10px] bg-white/70 mb-3 overflow-hidden">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-3 px-3.5 py-3">
                <Field label="Payer" value={payer.payer_name} />
                <Field label="Check #" value={pay.check_number} mono />
                <Field label="Check Date" value={fmtDate(pay.check_date)} />
                <Field label="Check Amount" value={pay.check_amount != null ? money(pay.check_amount) : null} />
                <Field label="Method" value={pay.payment_method} />
                <Field label="Statement Date" value={fmtDate(pay.statement_date)} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-3 px-3.5 py-3 border-t border-black/[.06] bg-black/[.015]">
                <div className="col-span-2 lg:col-span-2 min-w-0">
                  <div className="text-[8px] font-bold tracking-[.05em] uppercase text-[#6e6e73]">Payee · name</div>
                  <div className="text-[11px] font-bold text-[#1d1d1f] mt-[2px] truncate">{payee.payee_name || "—"}</div>
                </div>
                <Field label="NPI" value={payee.payee_npi} mono />
                <Field label="Tax ID" value={payee.payee_tax_id} mono />
                {contract ? <Field label="Contract" value={contract} /> : null}
              </div>
            </div>

            {/* Claims */}
            <div className="flex items-center gap-2 text-[8.5px] font-extrabold tracking-[.05em] uppercase text-[#6e6e73] mb-2">
              <span>Claims ({visibleClaims.length}{activeCode ? ` of ${claims.length}` : ""})</span>
              {activeCode ? (
                <button onClick={() => setActiveCode(null)} data-testid={TID.eraViewClearFilter}
                  className="inline-flex items-center gap-1 normal-case tracking-normal rounded-full bg-[#1d1d1f] text-white px-2 py-[3px] text-[9px] font-bold hover:opacity-90 transition">
                  <span className="font-mono">{activeCode}</span>
                  <X size={11} />
                </button>
              ) : null}
            </div>

            {visibleClaims.map(({ c, ci }) => {
              const st = claimStatus(c);
              const lines = Array.isArray(c.service_lines) ? c.service_lines : [];
              const isOpen = open.has(ci);
              const ct = c.claim_totals || {};
              const balanced = claimBalanced(c);
              const cAdj = lines.reduce((s, l) => s + lineAdjTotal(l), 0);
              return (
                <div key={ci} className="border border-black/[.08] rounded-[12px] overflow-hidden bg-white mb-2.5">
                  {/* Header */}
                  <div onClick={() => toggle(ci)} data-testid={TID.eraViewClaimRow(ci)}
                    className="flex items-center gap-3 px-3.5 py-3 cursor-pointer hover:bg-black/[.015]">
                    <span className={`w-[10px] h-[10px] rounded-full flex-shrink-0 ${STATE_DOT[st]}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-bold truncate">{c.patient_name || "Claim"}</div>
                      <div className="text-[9px] text-[#6e6e73] truncate mt-[1px] font-mono">
                        {c.claim_number ? `Claim ${c.claim_number}` : "Claim —"}
                        {c.patient_account_number ? ` · Acct ${c.patient_account_number}` : ""}
                      </div>
                      <span className={`inline-flex items-center gap-1 mt-1.5 text-[8.5px] font-bold rounded-[6px] px-[7px] py-[2px] ${balanced ? "text-[#1f9d57] bg-[#e7f6ee]" : "text-[#b06f00] bg-[#fbf1de]"}`}>
                        {balanced ? <CheckCircle2 size={10} strokeWidth={2.4} /> : null}
                        {balanced ? "Lines balanced" : "Lines need review"}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <Stat label="Billed" value={money(ct.billed_amount)} />
                      <Stat label="Paid" value={money(ct.paid_amount)} tone="grn" />
                      <Stat label="Pt resp" value={money(ct.patient_responsibility)} tone="amb" />
                      <ChevronRight size={14} className={`text-[#6e6e73] transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    </div>
                  </div>

                  {isOpen ? (
                    <div className="border-t border-black/[.06]">
                      {/* Patient / provider meta */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 px-3.5 py-3 bg-black/[.012]">
                        <Field label="Patient DOB" value={fmtDate(c.patient_dob)} />
                        <Field label="Patient ID" value={c.patient_id} mono />
                        <Field label="Provider" value={c.provider_name} />
                        <Field label="Provider NPI" value={c.provider_npi} mono />
                      </div>

                      {/* Claim money row */}
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 px-3.5 pt-3">
                        <MiniMoney label="Billed" value={money(ct.billed_amount)} />
                        <MiniMoney label="Allowed" value={money(ct.allowed_amount)} tone="blu" />
                        <MiniMoney label="Paid" value={money(ct.paid_amount)} tone="grn" />
                        <MiniMoney label="Adjustments" value={money(cAdj)} tone="amb" />
                        <MiniMoney label="Pt resp" value={money(ct.patient_responsibility)} tone="amb" />
                      </div>

                      {/* Service lines */}
                      <div className="px-3.5 py-3">
                        <div className="text-[8px] font-extrabold uppercase tracking-[.05em] text-[#6e6e73] mb-1.5">Service lines ({lines.length})</div>
                        <div className="border border-black/[.07] rounded-[10px] overflow-hidden">
                          <div className={`grid ${LINE_COLS} gap-2 px-3 py-2 text-[7px] font-extrabold uppercase tracking-[.03em] text-[#6e6e73] bg-black/[.03] border-b border-black/[.06]`}>
                            <span>DOS</span><span>CPT / HCPCS</span><span>Mods</span><span className="text-right">Units</span>
                            <span className="text-right">Billed</span><span className="text-right">Allowed</span><span className="text-right">Paid</span>
                            <span className="text-right">Pt resp</span><span className="text-right">Adjust.</span><span>Remark</span><span className="text-center">Bal</span>
                          </div>
                          {lines.map((l, li) => {
                            const adjs = l.adjustments || [];
                            const adj = lineAdjTotal(l);
                            const bal = lineBalanced(l);
                            const lp = num(l.paid_amount);
                            const prAdjs = adjs.filter(isPR);
                            // Remark: real remark codes if present, else the decoded CARC set
                            // (group tag + full code, e.g. "CO, CO-45, OA, OA-23, PR, PR-3").
                            const rmk = [];
                            const rseen = new Set();
                            const push = (x) => { if (x && !rseen.has(x)) { rseen.add(x); rmk.push(x); } };
                            (l.remark_codes || []).forEach(push);
                            adjs.forEach((a) => { push(a.group_code); push(fullCode(a)); });
                            const remark = rmk.join(", ");
                            return (
                              <div key={li} className="border-b border-black/[.05] last:border-b-0">
                                <div className={`grid ${LINE_COLS} gap-2 px-3 py-2 text-[9.5px] items-center`}>
                                  <span className="truncate">{fmtDateShort(l.date_of_service) || "—"}</span>
                                  <span className="font-mono font-semibold truncate">{l.cpt_code || "—"}</span>
                                  <span className="truncate text-[#6e6e73]">{l.modifier || "–"}</span>
                                  <span className="text-right tabular-nums">{l.units ?? "–"}</span>
                                  <span className="text-right tabular-nums">{money(l.billed_amount)}</span>
                                  <span className="text-right tabular-nums">{money(l.allowed_amount)}</span>
                                  <span className={`text-right tabular-nums font-bold ${lp > 0 ? "text-[#1f9d57]" : ""}`}>{money(l.paid_amount)}</span>
                                  <span className="text-right tabular-nums">{money(l.patient_responsibility)}</span>
                                  <span className={`text-right tabular-nums ${adj > 0 ? "text-[#d23b2f]" : "text-[#c7c7cc]"}`}>{adj > 0 ? money(adj) : "—"}</span>
                                  <span className="font-mono truncate text-[#6e6e73]">{remark || "–"}</span>
                                  <span className="flex justify-center">
                                    {bal ? <CheckCircle2 size={13} className="text-[#1f9d57]" strokeWidth={2.2} /> : <span className="text-[#c7c7cc]">—</span>}
                                  </span>
                                </div>
                                {adjs.length ? (
                                  <div className="flex items-start gap-x-4 gap-y-1.5 flex-wrap px-3 pt-2 pb-2 bg-black/[.012]">
                                    {prAdjs.length ? (
                                      <div className="flex items-center gap-2">
                                        <span className="text-[7px] font-extrabold uppercase tracking-[.04em] text-[#6e6e73]">Patient resp</span>
                                        <span className="flex gap-1 flex-wrap">
                                          {prAdjs.map((a, ai) => (
                                            <Dec key={ai} code={fullCode(a)} arrow cls="pat"
                                              desc={`${a.description ? a.description + " " : ""}${money(Math.abs(num(a.amount)))}`} />
                                          ))}
                                        </span>
                                      </div>
                                    ) : null}
                                    <div className="flex items-center gap-2">
                                      <span className="text-[7px] font-extrabold uppercase tracking-[.04em] text-[#6e6e73]">Adjustments</span>
                                      <span className="flex gap-1 flex-wrap">
                                        {adjs.map((a, ai) => (
                                          <Dec key={ai} code={fullCode(a)} desc={money(Math.abs(num(a.amount)))} cls={decClass(fullCode(a), a.group_code)} />
                                        ))}
                                      </span>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}

            {/* Route note */}
            <div className="flex items-center gap-2 text-[11px] bg-[#ececfb] border border-[rgba(88,86,214,.2)] rounded-[10px] px-3 py-[9px] my-3.5">
              <span className="text-[#5856D6] font-extrabold">→</span>
              <span>
                <b className="font-bold">{money(totals.total_patient_responsibility)}</b> patient responsibility routes to <b className="font-bold">Patient Billing</b> on post. Contractual CO-adjustments are written off
                {deniedCount ? <>; the <b className="font-bold">{deniedCount} denial{deniedCount === 1 ? "" : "s"}</b> {deniedCount === 1 ? "is" : "are"} staged for appeal</> : null}.
              </span>
            </div>

            {/* Denial & remark notes */}
            {denialNotes.length ? (
              <div className="border border-[rgba(210,59,47,.22)] bg-[#fbe9e7] rounded-[12px] px-3.5 py-3 mb-3">
                <div className="text-[11px] font-extrabold text-[#d23b2f] mb-1.5">⚠ Denial &amp; remark notes · decoded</div>
                {denialNotes.map((n, i) => (
                  <div key={i} className="text-[10.5px] leading-[1.45] py-[5px] border-t border-[rgba(210,59,47,.14)] first:border-t-0">{n}</div>
                ))}
              </div>
            ) : null}
          </div>

          {/* RIGHT rail */}
          <div>
            <div>
              {/* Legend — click a code to filter the claims list */}
              {legend.length ? (
                <div className="border border-black/[.08] bg-white/70 rounded-[12px] px-3.5 py-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <ListFilter size={12} className="text-[#5856D6]" />
                    <div className="text-[9px] font-extrabold tracking-[.04em] uppercase text-[#6e6e73]">Codes on this check</div>
                  </div>
                  <div className="text-[8.5px] text-[#8e8e93] mb-2.5 leading-[1.4]">Tap any code to filter the claims to just the ones carrying it.</div>
                  <div className="space-y-1">
                    {legend.map((l, i) => {
                      const isActive = activeCode === l.code;
                      const codeCls = l.cls === "pat" ? "text-[#b06f00]" : l.cls === "appeal" ? "text-[#d23b2f]" : "text-[#1d1d1f]";
                      return (
                        <button key={i} type="button" onClick={() => toggleCode(l.code)}
                          data-testid={TID.eraViewLegendCode(l.code)}
                          aria-pressed={isActive}
                          className={`group w-full text-left rounded-[10px] px-2.5 py-2 border transition cursor-pointer ${
                            isActive ? "border-[#5856D6] bg-[#5856D6]/[.07] ring-1 ring-[#5856D6]/30"
                              : "border-black/[.07] bg-white/60 hover:border-[#5856D6]/50 hover:bg-[#5856D6]/[.04]"
                          }`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className={`font-mono font-extrabold text-[10.5px] ${codeCls}`}>{l.code}</span>
                            <span className="text-[10.5px] font-extrabold tabular-nums text-[#1d1d1f]">{money(l.total)}</span>
                          </div>
                          {l.desc ? (
                            <div className="text-[9.5px] text-[#6e6e73] leading-[1.35] mt-0.5">{l.desc}</div>
                          ) : null}
                          <div className={`flex items-center gap-1 mt-1 text-[8.5px] font-semibold ${isActive ? "text-[#5856D6]" : "text-[#a1a1a6] group-hover:text-[#5856D6]"}`}>
                            {isActive ? (
                              <><X size={10} /> Filtering · tap to clear</>
                            ) : (
                              <><ListFilter size={10} /> {l.claims.size} claim{l.claims.size === 1 ? "" : "s"} · filter</>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER — Experience Mode only */}
      {variant === "experience" ? (
      <div className="sticky bottom-0 z-20 flex items-center gap-3.5 px-[22px] py-[13px] bg-[#1d1d1f] text-white">
        <div>
          <div className="text-[12px] font-extrabold">Experience Mode</div>
          <div className="text-[9.5px] text-white/60 mt-[1px]">runs the real pipeline, never submits</div>
        </div>
        <div className="flex gap-[5px] ml-1">
          <i className="w-[22px] h-[5px] rounded-[3px] bg-white" />
          <i className="w-[22px] h-[5px] rounded-[3px] bg-white/20" />
          <i className="w-[22px] h-[5px] rounded-[3px] bg-white/20" />
          <i className="w-[22px] h-[5px] rounded-[3px] bg-white/20" />
        </div>
        <span className="text-[9.5px] text-white/60">1 of 4 demos explored</span>
        <div className="ml-auto flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-[10px] text-white/60">
            Rate revQ
            <button onClick={() => setFeedback("up")} aria-label="Thumbs up"
              className={`w-[26px] h-[26px] rounded-[7px] border border-white/20 flex items-center justify-center transition ${feedback === "up" ? "bg-white text-[#1d1d1f]" : "bg-white/[.08] hover:bg-white/20"}`}>
              <ThumbsUp size={13} />
            </button>
            <button onClick={() => setFeedback("down")} aria-label="Thumbs down"
              className={`w-[26px] h-[26px] rounded-[7px] border border-white/20 flex items-center justify-center transition ${feedback === "down" ? "bg-white text-[#1d1d1f]" : "bg-white/[.08] hover:bg-white/20"}`}>
              <ThumbsDown size={13} />
            </button>
          </span>
          <button onClick={onChooseFlow} data-testid={TID.eraViewChooseFlowBtn}
            className="text-[11px] font-bold rounded-[9px] px-[13px] py-2 border border-white/25 hover:bg-white/10 transition">Try another</button>
          <button onClick={onFinish} data-testid={TID.eraViewFinishBtn}
            className="text-[11px] font-bold rounded-[9px] px-[13px] py-2 bg-white text-[#1d1d1f] border border-white hover:opacity-90 transition">Finish setup →</button>
        </div>
      </div>
      ) : null}
    </div>
  );
}

function Field({ label, value, mono }) {
  const empty = value == null || value === "";
  return (
    <div className="min-w-0">
      <div className="text-[8px] font-bold tracking-[.05em] uppercase text-[#6e6e73]">{label}</div>
      <div className={`text-[11px] mt-[2px] truncate ${empty ? "text-[#c7c7cc] font-medium" : "text-[#1d1d1f] font-bold"} ${mono && !empty ? "font-mono tabular-nums" : ""}`}>
        {empty ? "—" : value}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const c = tone === "grn" ? "text-[#1f9d57]" : tone === "amb" ? "text-[#b06f00]" : "text-[#1d1d1f]";
  return (
    <div className="text-right">
      <div className={`text-[12px] font-extrabold tabular-nums leading-none ${c}`}>{value}</div>
      <div className="text-[7px] font-bold uppercase tracking-[.05em] text-[#6e6e73] mt-1">{label}</div>
    </div>
  );
}

function MiniMoney({ label, value, tone }) {
  const c = tone === "blu" ? "text-[#2563a8]" : tone === "grn" ? "text-[#1f9d57]" : tone === "amb" ? "text-[#b06f00]" : "text-[#1d1d1f]";
  return (
    <div className="border border-black/[.07] rounded-[9px] bg-black/[.015] px-2.5 py-2">
      <div className="text-[7px] font-bold uppercase tracking-[.04em] text-[#6e6e73]">{label}</div>
      <div className={`text-[13px] font-extrabold tabular-nums mt-0.5 ${c}`}>{value}</div>
    </div>
  );
}

function Money({ label, value, tone, note }) {
  const toneCls = tone === "blu" ? "text-[#2563a8]" : tone === "grn" ? "text-[#1f9d57]" : tone === "amb" ? "text-[#b06f00]" : "text-[#1d1d1f]";
  return (
    <div className="flex-1 min-w-[140px] border border-black/[.08] rounded-[12px] bg-white/70 px-[13px] py-[11px]">
      <div className="text-[8px] font-bold tracking-[.04em] uppercase text-[#6e6e73]">{label}</div>
      <div className={`text-[18px] font-extrabold tracking-[-.02em] mt-1 tabular-nums ${toneCls}`}>{value}</div>
      {note ? <div className="text-[8px] text-[#6e6e73] mt-[1px] truncate">{note}</div> : null}
    </div>
  );
}
